import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Navbar from './common/Navbar.jsx';
import Footer from './common/Footer.jsx';
import PoliceLoginModal from './modals/PoliceLoginModal.jsx';
import CitizenModal from './modals/CitizenModal.jsx';

const Layout = () => {
  const [policeModalOpen, setPoliceModalOpen] = useState(false);
  const [citizenModalOpen, setCitizenModalOpen] = useState(false);
  const [liveClock, setLiveClock] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setLiveClock(
        now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) + ' IST'
      );
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.body.style.overflow = policeModalOpen || citizenModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [policeModalOpen, citizenModalOpen]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPoliceModalOpen(false);
        setCitizenModalOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const openPolice = () => setPoliceModalOpen(true);
    const openCitizen = () => setCitizenModalOpen(true);
    window.addEventListener('openPoliceModal', openPolice);
    window.addEventListener('openCitizenModal', openCitizen);
    return () => {
      window.removeEventListener('openPoliceModal', openPolice);
      window.removeEventListener('openCitizenModal', openCitizen);
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.replace('#', ''));
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  return (
    <div className="cyber-grid">
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/8 rounded-full blur-3xl floating" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/8 rounded-full blur-3xl floating" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl floating" style={{ animationDelay: '4s' }} />
      </div>

      <div className="bg-white border-b border-cyan-500/20 px-6 py-2.5 flex justify-between items-center text-xs text-gray-600">
        <div className="flex items-center gap-3">
          <span className="live-dot" />
          <span className="mono">NATIONAL CYBER MONITORING NETWORK — ACTIVE</span>
        </div>
        <div className="hidden md:flex items-center gap-6 mono">
          <span><i className="fa-solid fa-clock text-cyan-400" /> 24×7 Monitoring</span>
          <span><i className="fa-solid fa-globe text-cyan-400" /> Pan India Coverage</span>
          <span className="text-cyan-700">{liveClock}</span>
        </div>
      </div>

      <Navbar onPoliceLogin={() => setPoliceModalOpen(true)} onCitizenPortal={() => setCitizenModalOpen(true)} liveClock={liveClock} />

      <main>
        <Outlet />
      </main>

      <Footer />

      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-2xl shadow-cyan-500/30 ${showScrollTop ? 'flex' : 'hidden'} items-center justify-center text-lg hover:scale-110 transition-all`}
      >
        <i className="fa-solid fa-arrow-up" />
      </button>

      <PoliceLoginModal isOpen={policeModalOpen} onClose={() => setPoliceModalOpen(false)} />
      <CitizenModal isOpen={citizenModalOpen} onClose={() => setCitizenModalOpen(false)} />
    </div>
  );
};

export default Layout;
